import styled, { css } from 'styled-components';
import { yellow } from '@zocdoc/branding/colors';
import { breakpoint } from '../grid';
import { FontFamily } from '../typography';
import { UniversalSearchPrimitives } from '../universalsearch';
import { MagnifyingGlass, Arrow } from '../icons';
import { INPUT_HEIGHT } from '../picker-constants';
import {
    InsurancePickerViewPrimitives,
    SelectedStateViewPrimitives,
    ThemedInsurancePicker as InsurancePicker,
} from '../InsurancePicker';
import { DropdownPrimitives } from '../TakeoverInput';

const MAX_EXPANDABLE_HEIGHT = 100; // Must be >= the tallest expandable; the animation becomes faster for larger numbers

const fieldOverrides = css`
    background-color: ${props => props.theme.search.bar.field.backgroundColor};
    border-color: ${props => props.theme.search.bar.field.borderColor};
    color: ${props => props.theme.search.bar.field.color};

    &::placeholder {
        color: ${props => props.theme.search.bar.field.placeholderColor};
    }

    &:focus::placeholder {
        color: ${props => props.theme.search.bar.field.placeholderColorFocus};
    }

    &:focus {
        background-color: ${props => props.theme.search.bar.field.backgroundColorFocus};
        border-color: ${props => props.theme.search.bar.field.borderColorFocus};
        color: ${props => props.theme.search.bar.field.colorFocus};
    }
`;

const SearchBarContainer = styled.div`
    box-sizing: border-box;
    position: relative;
`;

const SearchBarWrapper = styled.div`
    background-color: ${yellow};
    padding-bottom: 20px;
`;

const SearchForm = styled.form`
    display: flex;
    flex-direction: column;
    max-width: 100%;

    ${InsurancePickerViewPrimitives.InsurancePickerTextbox},
    ${SelectedStateViewPrimitives.SelectedState} {
        ${fieldOverrides};
    }

    ${DropdownPrimitives.Wrapper},
    ${UniversalSearchPrimitives.Container} {
        &[data-dropdown-active="true"] {
            ${breakpoint.small} {
                [data-test="universal-search-text-box"],
                ${DropdownPrimitives.FieldInput} {
                    ${fieldOverrides};
                }
            }
        }

        &[data-dropdown-active="false"] {
            [data-test="universal-search-text-box"],
            ${DropdownPrimitives.FieldInput} {
                ${fieldOverrides};
            }
        }
    }

    ${breakpoint.medium} {
        flex-direction: row;
    }
`;

const Expandable = styled.div`
    ${breakpoint('small', { down: true })} {
        max-height: ${props => props.expanded ? MAX_EXPANDABLE_HEIGHT : '0'}px;
        opacity: ${props => props.expanded ? '1' : '0'};
        transition: ${props => props.expanded ? 'all 0.5s' : 'none'};
    }
`;

const SearchFieldWrapper = styled.div`
    flex-grow: 1;
    line-height: 0;
    position: relative; // To contain the toggle

    ${breakpoint.medium} {
        &:not(:first-child) {
            margin-left: 1px;
        }
    }
`;

const SearchFieldLabel = styled.h4`
    font: 13px/1.3 ${FontFamily.SharpSansSemibold};
    padding-bottom: 9px;
    padding-top: 14px;

    ${breakpoint.medium} {
        display: none;
    }
`;

const ExpandableSearchFieldWrapper = Expandable.withComponent(SearchFieldWrapper);

const PpsSearchFieldWrapper = styled(SearchFieldWrapper)`
    ${breakpoint.medium} {
        flex-grow: 11;
    }
`;

const LocationSearchFieldWrapper = styled(ExpandableSearchFieldWrapper)`
    ${breakpoint.medium} {
        flex-grow: 5;
    }
`;

const InsuranceSearchFieldWrapper = styled(ExpandableSearchFieldWrapper)`
    ${breakpoint.medium} {
        flex-grow: 7;
    }
`;

const fontSize = 24;

const SearchButton = styled.button`
    background-color: ${props => props.theme.search.bar.button.bgColor};
    border: 0;
    border-radius: 0;
    color: ${props => props.theme.search.bar.button.iconColor};
    cursor: pointer;
    font: ${fontSize}px/1 ${FontFamily.SharpSansBold};
    margin-top: 16px;
    padding: ${(INPUT_HEIGHT - fontSize) / 2}px 0;
    text-align: center;
    width: 100%;

    &:hover {
        color: ${props => props.theme.search.bar.button.iconColorHover};
        background-color: ${props => props.theme.search.bar.button.bgColorHover};
    }

    &:active {
        color: ${props => props.theme.search.bar.button.iconColorPressed};
        background-color: ${props => props.theme.search.bar.button.bgColorPressed};
    }

    ${breakpoint.medium} {
        margin-top: 0;
        padding: 0;
        height: ${INPUT_HEIGHT}px;
        width: ${INPUT_HEIGHT}px;
    }
`;

const SearchButtonText = styled.span`
    ${breakpoint.medium} {
        display: none;
    }
`;

const StyledInsurancePicker = styled(InsurancePicker)``;

const StyledMagnifyingGlass = styled(MagnifyingGlass)`
    box-sizing: content-box !important;
    display: none;
    font-size: 24px;
    width: 100%;

    ${breakpoint.medium} {
        display: block;
    }
`;

const CollapseToggle = styled.span`
    display: inline-block;
    position: relative;
    bottom: -8px;
    padding: 4px;
    text-align: right;
    cursor: pointer;

    ${breakpoint.medium} {
        display: none;
    }
`;

const TextRight = styled.div`
    text-align: right;
`;

const StyledArrow = styled(Arrow)`
    font-size: 8px;
`;

const StyledClose = styled.span`
    margin-left: 10px;
    font: 13px/1.3 ${FontFamily.SharpSansSemibold};
`;

const Toggle = styled.div`
    z-index: 1;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    cursor: text;

    ${breakpoint.medium} {
        display: none;
    }
`;

export {
    CollapseToggle,
    Expandable,
    ExpandableSearchFieldWrapper,
    InsuranceSearchFieldWrapper,
    LocationSearchFieldWrapper,
    PpsSearchFieldWrapper,
    SearchBarContainer,
    SearchBarWrapper,
    SearchButton,
    SearchButtonText,
    SearchFieldLabel,
    SearchFieldWrapper,
    SearchForm,
    StyledArrow,
    StyledClose,
    StyledInsurancePicker,
    StyledMagnifyingGlass,
    TextRight,
    Toggle,
};
